/**
 * Pipelines API client — o processo seletivo configurável (migration 0023, W1).
 *
 * Espelha `lib/api/job-profiles.ts`: helpers finos sobre
 * `apps/api/src/routes/pipelines.ts`, com o cookie de sessão
 * (`credentials: 'include'`) e leitura de erro em RFC 7807 pelo
 * `readProblemMessage` partilhado.
 *
 * Um pipeline é o MOLDE do processo: a vaga escolhe-o (W2) e a candidatura
 * percorre-o (W3). As etapas têm funções próprias, e não um campo de update,
 * porque cada gesto tem regra própria do lado do servidor.
 *
 * ERROS: nenhuma classe de erro tipada aqui, ao contrário de
 * `JobProfileLinkedError`. Os dois 409 desta API (arquivar o padrão, arquivar a
 * última etapa ativa) não trazem número nenhum a extrair — trazem uma frase que
 * diz o que fazer, e a interface mostra-a tal como veio.
 */
import { API_BASE } from '@/lib/api'
import { readProblemMessage } from '@/lib/api/problem'

export interface PipelineStage {
  id: string
  pipelineId: string
  name: string
  position: number
  /** `null` = ativa. Uma etapa arquivada não aceita cartões novos (W4). */
  archivedAt: string | null
  /** ISO-8601 (o `c.json` do Hono serializa as colunas de data assim). */
  createdAt: string
  updatedAt: string
}

export interface Pipeline {
  id: string
  name: string
  description: string | null
  /** No máximo um em todo o cadastro — garantido por índice único parcial. */
  isDefault: boolean
  archivedAt: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
  /** Só as ATIVAS — é o número que diz o tamanho do processo. */
  stageCount: number
  /**
   * Quantas vagas usam este processo (migration 0024, onda W2). Sem filtro de
   * estado da vaga: a pergunta é "isto ainda está em uso?", e uma vaga fechada
   * continua a precisar do molde para o seu histórico fazer sentido.
   *
   * SÓ NA LISTAGEM — ver a nota do `PipelineDetail` abaixo.
   */
  opportunityCount: number
}

/**
 * O detalhe traz as etapas TODAS, arquivadas incluídas.
 *
 * `stageCount` não vem: seria a contagem de uma lista que o chamador já tem
 * inteira na mão, e duas fontes para o mesmo número divergem. `opportunityCount`
 * também não: o detalhe é a tela de EDIÇÃO do molde, e quantas vagas o usam é
 * informação do catálogo, não do editor.
 */
export interface PipelineDetail extends Omit<Pipeline, 'stageCount' | 'opportunityCount'> {
  stages: PipelineStage[]
}

export interface PipelinesListResponse {
  items: Pipeline[]
  page: number
  pageSize: number
  total: number
}

export interface CreatePipelineInput {
  name: string
  description?: string | null
  isDefault?: boolean
  /** Pelo menos uma — um pipeline sem etapas não tem onde pôr ninguém. */
  stages: { name: string }[]
}

export interface UpdatePipelineInput {
  name?: string
  description?: string | null
  isDefault?: boolean
  /** `false` desarquiva — é a volta que o `archivePipeline` não tem. */
  archived?: boolean
}

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' }

const acceptJson = { Accept: 'application/json' }

/**
 * GET /api/pipelines — lista paginada.
 *
 * `archived: true` TROCA a lista pelos arquivados (não os acrescenta): é o que
 * o segmented control de dois valores da tela precisa.
 */
export async function listPipelines(
  params: {
    archived?: boolean
    page?: number
    pageSize?: number
    signal?: AbortSignal
  } = {},
): Promise<PipelinesListResponse> {
  const url = new URL(`${API_BASE}/pipelines`, window.location.origin)
  if (params.archived) url.searchParams.set('archived', 'true')
  url.searchParams.set('page', String(params.page ?? 1))
  url.searchParams.set('pageSize', String(params.pageSize ?? 50))

  const init: RequestInit = { credentials: 'include', headers: acceptJson }
  if (params.signal) init.signal = params.signal

  const res = await fetch(`${url.pathname}${url.search}`, init)
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível carregar os pipelines (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  return (await res.json()) as PipelinesListResponse
}

/** GET /api/pipelines/:id — a linha + as etapas (arquivadas incluídas). */
export async function getPipeline(id: string, signal?: AbortSignal): Promise<PipelineDetail> {
  const init: RequestInit = { credentials: 'include', headers: acceptJson }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/pipelines/${encodeURIComponent(id)}`, init)
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível carregar o pipeline (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  return (await res.json()) as PipelineDetail
}

/** POST /api/pipelines — cria o pipeline E as etapas num pedido só. */
export async function createPipeline(body: CreatePipelineInput): Promise<PipelineDetail> {
  const res = await fetch(`${API_BASE}/pipelines`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível criar o pipeline (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  return (await res.json()) as PipelineDetail
}

/**
 * PATCH /api/pipelines/:id — identidade e o interruptor de padrão.
 *
 * Devolve a linha SEM as etapas (o endpoint não lhes toca). Daí o `Omit`: quem
 * precisa das etapas depois disto refaz o `getPipeline`.
 */
export async function updatePipeline(
  id: string,
  body: UpdatePipelineInput,
): Promise<Omit<Pipeline, 'stageCount' | 'opportunityCount'>> {
  const res = await fetch(`${API_BASE}/pipelines/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível salvar o pipeline (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  return (await res.json()) as Omit<Pipeline, 'stageCount' | 'opportunityCount'>
}

/**
 * DELETE /api/pipelines/:id — ARQUIVA. Nunca apaga.
 *
 * O nome diz o que acontece, e não o método HTTP que o provoca: quem lê
 * `archivePipeline(id)` na tela não fica com a ideia de que o histórico das
 * vagas se perde.
 */
export async function archivePipeline(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/pipelines/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: acceptJson,
  })
  if (res.ok) return
  throw new Error(
    await readProblemMessage(res, `Não foi possível arquivar o pipeline (${res.status})`, [
      'detail',
      'title',
    ]),
  )
}

/** POST /api/pipelines/:id/stages — acrescenta ao fim. */
export async function addPipelineStage(pipelineId: string, name: string): Promise<PipelineStage> {
  const res = await fetch(`${API_BASE}/pipelines/${encodeURIComponent(pipelineId)}/stages`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ name }),
  })
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível criar a etapa (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  return (await res.json()) as PipelineStage
}

/** PATCH /api/pipelines/:id/stages/:stageId — renomeia. */
export async function renamePipelineStage(
  pipelineId: string,
  stageId: string,
  name: string,
): Promise<PipelineStage> {
  const res = await fetch(
    `${API_BASE}/pipelines/${encodeURIComponent(pipelineId)}/stages/${encodeURIComponent(stageId)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: jsonHeaders,
      body: JSON.stringify({ name }),
    },
  )
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível renomear a etapa (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  return (await res.json()) as PipelineStage
}

/**
 * DELETE /api/pipelines/:id/stages/:stageId — arquiva a etapa.
 *
 * O 409 da última etapa ativa chega aqui como `Error` com a FRASE DO SERVIDOR
 * ("Um pipeline precisa de pelo menos uma etapa ativa…"). A tela mostra-a como
 * veio: reescrevê-la no cliente seria manter duas versões da mesma regra.
 */
export async function archivePipelineStage(pipelineId: string, stageId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/pipelines/${encodeURIComponent(pipelineId)}/stages/${encodeURIComponent(stageId)}`,
    { method: 'DELETE', credentials: 'include', headers: acceptJson },
  )
  if (res.ok) return
  throw new Error(
    await readProblemMessage(res, `Não foi possível arquivar a etapa (${res.status})`, [
      'detail',
      'title',
    ]),
  )
}

/**
 * PUT /api/pipelines/:id/stages/order — a lista COMPLETA das etapas ativas.
 *
 * Completa, e não só as que mudaram: é o que deixa o servidor verificar que o
 * conjunto bate certo, e o que torna a escrita atómica.
 */
export async function reorderPipelineStages(pipelineId: string, stageIds: string[]): Promise<void> {
  const res = await fetch(`${API_BASE}/pipelines/${encodeURIComponent(pipelineId)}/stages/order`, {
    method: 'PUT',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ stageIds }),
  })
  if (res.ok) return
  throw new Error(
    await readProblemMessage(res, `Não foi possível reordenar as etapas (${res.status})`, [
      'detail',
      'title',
    ]),
  )
}
